import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import type { ImmunizationCoding } from '@/types/patient-chart-api';

/**
 * Three small static master-data lookups for the Immunizations Add
 * flow (PROD-827 chart write surfaces). All curl-verified by Andrew
 * 2026-05-20.
 *
 *   GET /api/Master/AdministrationSite         → site picker
 *   GET /api/Master/VaccinationDurationUnits   → dose unit picker
 *   GET /api/Master/VaccinationDurationQuantity → dose ordinal picker
 *
 * Each returns `ImmunizationCoding[]` (6-field shape with PascalCase
 * `Definition`). Cached aggressively (15 min) — these change rarely.
 *
 * Grouped into one file rather than three because they're consumed
 * together by the same `<AddImmunizationEditor>` and the lookups all
 * share the same shape + caching policy. If a future picker grows
 * search semantics, split it into its own file (mirrors the
 * useVaccineCodeSearch split).
 */

const STALE_15_MIN = 15 * 60 * 1000;

export function useAdministrationSiteList(enabled = true) {
  return useQuery({
    queryKey: ['master-administration-site'],
    queryFn: ({ signal }) =>
      get<ImmunizationCoding[]>('/api/Master/AdministrationSite', { signal }),
    enabled,
    staleTime: STALE_15_MIN,
  });
}

export function useVaccinationDurationUnits(enabled = true) {
  return useQuery({
    queryKey: ['master-vaccination-duration-units'],
    queryFn: ({ signal }) =>
      get<ImmunizationCoding[]>('/api/Master/VaccinationDurationUnits', {
        signal,
      }),
    enabled,
    staleTime: STALE_15_MIN,
  });
}

export function useVaccinationDurationQuantity(enabled = true) {
  return useQuery({
    queryKey: ['master-vaccination-duration-quantity'],
    queryFn: ({ signal }) =>
      get<ImmunizationCoding[]>('/api/Master/VaccinationDurationQuantity', {
        signal,
      }),
    enabled,
    staleTime: STALE_15_MIN,
  });
}
